import { useAuth0 } from '@auth0/auth0-react';
import { DropdownMenuComponent, DropdownMenuItemType } from '@components/core/dropdown-menu.component';
import { ShareReferralLinkModal } from '@components/pages/home/components/home-share-referral-link.component';
import { UserProfileModal } from '@components/pages/home/components/home-user-profile-modal.component';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@components/ui/alert-dialog';
import { useApi } from '@hooks/api.hook';
import { useGetCurrentUser, useResetMyData } from '@queries/users.query.ts';
import * as Portal from '@radix-ui/react-portal';
import { clearAppCache } from '@utils/auth.util';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useSidebar } from '../ui/sidebar';
import { AvatarItem } from './avatar-item.component';

const UserComponent: FunctionComponent = () => {
  const { data: user, error } = useGetCurrentUser();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const { apiPost } = useApi({ showToastOnError: false });
  const resetMyDataMutation = useResetMyData();
  const [openAlert, setOpenAlert] = useState(false);
  const [userProfileModalOpen, setUserProfileModalOpen] = useState(false);
  const { state } = useSidebar();
  const [shareReferralModalOpen, setShareReferralModalOpen] = useState(false);

  const onClickEditProfile = () => {
    setUserProfileModalOpen(true);
  };

  const onCloseUserProfileModal = () => {
    setUserProfileModalOpen(false);
  };

  const handleLogout = async () => {
    try {
      await apiPost({
        path: '/v1/users/me/stop-impersonation',
      });
    } catch {}

    clearAppCache({ isLogout: true });
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const onCloseShareReferralModal = () => {
    setShareReferralModalOpen(false);
  };

  const handleResetMyData = async () => {
    try {
      clearAppCache({ isLogout: true });
      await resetMyDataMutation.mutateAsync();
      navigate('/');
    } catch (error) {
      console.error('Error resetting data:', error);
    }
  };

  if (error && user) {
    return (
      <div>
        <p>Error loading user data</p>
      </div>
    );
  }

  const name = user?.profile
    ? [user?.profile?.firstName, user?.profile?.lastName].filter(Boolean).join(' ')
    : user?.email.split('@')[0];
  const email = user?.email;

  const dropdownItems = [
    {
      text: 'Refer Users to Tyr',
      onClick: () => setShareReferralModalOpen(true),
      className: 'cursor-pointer',
    },
    {
      text: 'Edit Profile',
      onClick: onClickEditProfile,
      className: 'cursor-pointer',
      disabled: !user?.profile,
    },
    {
      text: 'Sign Out',
      onClick: () => {
        void handleLogout();
      },
      className: 'cursor-pointer',
      disabled: Boolean(user?.impersonation),
      tooltip: user?.impersonation ? 'You must stop impersonation before logging out' : undefined,
    },
  ].filter((i) => i !== null) satisfies DropdownMenuItemType[];

  const userHeader = (
    <div className="space-y-0 min-w-0 w-full">
      <div className="font-medium text-gray-900 truncate">{name ?? 'Loading...'}</div>
      <div className="text-xs text-gray-500 truncate">{email ?? ''}</div>
    </div>
  );

  return (
    <div className={cn('w-full flex items-center justify-start pl-1', state === 'collapsed' && 'pl-0.5')}>
      <div
        className="w-full min-w-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuComponent
          className="max-w-[280px]"
          trigger={
            <div className="flex items-center cursor-pointer w-full min-w-0 overflow-hidden">
              <AvatarItem
                altText={email ?? ''}
                colorMode="dark"
                imageUrl={user?.avatarUrl}
                initialText={name ?? ''}
                title={<span className="text-sm">{name ?? 'Loading...'}</span>}
                subtitle={email ?? ''}
              />
            </div>
          }
          header={userHeader}
          items={dropdownItems}
        />
      </div>
      <AlertDialog
        open={openAlert}
        onOpenChange={setOpenAlert}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to reset all your data?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetMyData}
              variant="danger"
            >
              Reset Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Portal.Root>
        <UserProfileModal
          isOpen={userProfileModalOpen}
          onClose={onCloseUserProfileModal}
          isEdit
        />
      </Portal.Root>
      {user?.profile?.referralCode && (
        <ShareReferralLinkModal
          isOpen={shareReferralModalOpen}
          onClose={onCloseShareReferralModal}
          referralCode={user.profile.referralCode}
        />
      )}
    </div>
  );
};

export default UserComponent;
